import Typography from "@mui/material/Typography";
import { GridColDef, GridOverlay, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TachographConnectivity, TachographRow, TachographStatus } from "../models/Tachograph";
import { formatDay } from "../utilities/tachographDates";
import { dueRowClassName, DUE_ROW_SX, TACHOGRAPH_PAGE_SIZES } from "./tachographGridDefaults";
import { TachographStatusIcon } from "./TachographStatusIcon";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { LoadFailedOverlay } from "@/components/grid/LoadFailedOverlay";
import { NoRowsOverlay } from "@/components/grid/NoRowsOverlay";
import { formatDate } from "@/hooks/metrics/useFormattedDate";

type Props = {
  tab: "status" | "connectors";
  rows: TachographRow[];
  loading: boolean;
  /** True while a source of the rows is loading or failed. */
  exportDisabled: boolean;
  /** True once make and calibration were read; only then does an empty cell mean "no value". */
  canShowMissing: boolean;
  /** True when a source of the rows failed: an empty grid then says so instead of "no rows". */
  loadFailed: boolean;
  /** True when this DB has no tachograph diagnostics, so no connector status can be told. */
  diagnosticsMissing: boolean;
  /** Colours overdue rows red and rows due soon yellow. */
  highlightRows: boolean;
};

const STATUS_COLUMN_WIDTH = 90;

/** Without the diagnostics every connector status is unknown, so the connector list stays empty. */
function NoConnectorDiagnosticsOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="text.secondary">{t("tachograph.connectorDiagnosticsMissing")}</Typography>
    </GridOverlay>
  );
}

function selectNoRowsOverlay(
  tab: Props["tab"],
  loadFailed: boolean,
  diagnosticsMissing: boolean,
) {
  if (loadFailed) return LoadFailedOverlay;
  if (tab === "connectors" && diagnosticsMissing) return NoConnectorDiagnosticsOverlay;
  return NoRowsOverlay;
}

/** Offered in the column menu, but most users identify a vehicle by its name. */
const HIDDEN_BY_DEFAULT = { vin: false, licensePlate: false, serialNumber: false };

/** The vehicle table shown by the status and connector tabs. */
export function TachographVehicleGrid({
  tab,
  rows,
  loading,
  exportDisabled,
  canShowMissing,
  loadFailed,
  diagnosticsMissing,
  highlightRows,
}: Props) {
  const { t, i18n } = useTranslation();
  // Only claim "no value" once the readings are known; while loading, after a failure or without
  // the diagnostics the cell stays empty (the loading overlay and the error alert say why).
  const missingText = canShowMissing ? t("tachograph.noValueIn14Days") : "";

  const columns = useMemo<GridColDef<TachographRow>[]>(() => {
    const statusColumn = (field: "downloadStatus"): GridColDef<TachographRow> => ({
      field,
      headerName: t(`tachograph.columns.${field}`),
      width: STATUS_COLUMN_WIDTH,
      align: "center",
      headerAlign: "center",
      // Excel gets the translated text instead of the raw code.
      valueFormatter: (value: TachographStatus | undefined) =>
        t(`tachograph.statuses.${value ?? "unknown"}`),
      renderCell: (params: GridRenderCellParams<TachographRow, TachographStatus>) => (
        <TachographStatusIcon
          value={params.value}
          label={t(`tachograph.statuses.${params.value ?? "unknown"}`)}
        />
      ),
    });

    return [
      { field: "summary", headerName: t("tachograph.columns.summary"), minWidth: 260, flex: 1 },
      { field: "vin", headerName: t("tachograph.columns.vin"), width: 190 },
      { field: "licensePlate", headerName: t("tachograph.columns.licensePlate"), width: 130 },
      { field: "serialNumber", headerName: t("tachograph.columns.serialNumber"), width: 150 },
      { field: "driver", headerName: t("tachograph.columns.driver"), minWidth: 240, flex: 1 },
      {
        field: "manufacturer",
        headerName: t("tachograph.columns.manufacturer"),
        width: 140,
        valueFormatter: (value: string) => value || missingText,
      },
      statusColumn("downloadStatus"),
      {
        field: "connectorStatus",
        headerName: t("tachograph.columns.connectorStatus"),
        width: 100,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value: TachographConnectivity | undefined) =>
          t(`tachograph.connectivity.${value ?? "unknown"}`),
        renderCell: (params: GridRenderCellParams<TachographRow, TachographConnectivity>) => (
          <TachographStatusIcon
            value={params.value}
            label={t(`tachograph.connectivity.${params.value ?? "unknown"}`)}
          />
        ),
      },
      {
        field: "legalLimit",
        headerName: t("tachograph.columns.legalLimit"),
        minWidth: 220,
        flex: 1,
        type: "date",
        valueGetter: (_value, row) => row.dueDate,
        renderCell: params => params.row.legalLimit,
      },
      {
        field: "nextDownloadUtc",
        headerName: t("tachograph.columns.nextDownloadUtc"),
        minWidth: 220,
        flex: 1,
        type: "date",
        valueGetter: (_value, row) => row.dueDate,
        renderCell: params => params.row.nextDownloadUtc,
      },
      {
        field: "calibrationExpiryDate",
        headerName: t("tachograph.columns.calibrationExpiryDate"),
        minWidth: 220,
        flex: 1,
        type: "date",
        valueGetter: (_value, row) => row.calibrationExpiryDate,
        valueFormatter: (value: Date | null) =>
          value ? formatDay(value, i18n.language) : missingText,
      },
      {
        field: "remoteDownload",
        headerName: t("tachograph.columns.remoteDownload"),
        width: 150,
        valueFormatter: (value: boolean | undefined) =>
          value === undefined ? missingText : t(value ? "tachograph.remoteDownload.on" : "tachograph.remoteDownload.off"),
      },
      {
        field: "downloadTaskStatus",
        headerName: t("tachograph.columns.downloadTaskStatus"),
        width: 170,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value: number | undefined) => (value === undefined ? missingText : String(value)),
      },
      {
        field: "tachographStatus",
        headerName: t("tachograph.columns.tachographStatus"),
        width: 170,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value: number | undefined) => (value === undefined ? missingText : String(value)),
      },
      {
        field: "updateTime",
        headerName: t("tachograph.columns.updateTime"),
        width: 170,
        type: "date",
        valueGetter: (_value, row) => row.updateDate,
        valueFormatter: (value: Date | null) => formatDate(value ?? undefined, i18n.language),
      },
    ];
  }, [t, i18n.language, missingText]);

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      loading={loading}
      pagination
      disableRowSelectionOnClick
      pageSizeOptions={TACHOGRAPH_PAGE_SIZES}
      slots={{
        noRowsOverlay: selectNoRowsOverlay(tab, loadFailed, diagnosticsMissing),
        toolbar: ExportToolbar,
      }}
      slotProps={{ toolbar: { report: { title: t(`tachograph.tabs.${tab}`) }, exportDisabled } }}
      getRowClassName={highlightRows ? params => dueRowClassName(params.row.downloadStatus) : undefined}
      sx={DUE_ROW_SX}
      settingsKey="tachographVehicles"
      initialState={{
        density: "compact",
        sorting: { sortModel: [{ field: "updateTime", sort: "desc" }] },
        columns: { columnVisibilityModel: HIDDEN_BY_DEFAULT },
      }}
    />
  );
}
